/* eslint-disable jsx-a11y/anchor-is-valid */
import { motion } from "framer-motion";
import secondaryComponents from "../..";
import styles from "./CompositeDetailsForm.module.scss";
import primaryComponents from "../../../primaryComponents";
import { Validation } from "./Validation";
import Icon from "../../../../assets/icons";
import { useEffect, useMemo, useRef, useState } from "react";
import sharedStyles from "../../SingleProductForm/SingleProductForm.module.scss";
import globalStyles from "../../../../assets/styles/base/globalPage.module.scss";
import { PricingWarning, WarningDialog } from "../../../common";
import { useNavigate } from "react-router-dom";
import { pathConstants } from "../../../../routes/pathContants";
import { ArrowCircle } from "../../../../assets/svgIcons";
import { useGetOrderBatchesCostPrice } from "../../../../hooks/api/queries";
import { Vatform } from "../../SingleProductForm/VatForm";
import { CategoryGroup } from "../../SingleProductForm/CategoryGroup";
import ProductImageUploader from "../../ProductImageUploader";
import { useParams } from "react-router-dom";
import { reorderProductImages } from "../../../../services/inventory";
import { useMutation } from "react-query";

const CompositeDetailsForm = ({ compositeProperties }) => {
  const navigate = useNavigate();
  const { mutate: reorderImages } = useMutation((data) =>
    reorderProductImages(data),
  );
  const {
    setCurrentStep,
    createOrUpdateProduct,
    rootFormDetails,
    storeProperties,
    setIsStorePropertiesUpdated,
    setRootFormDetails,
    setStoreProperties,
    isEditMode,
    isSubmiting,
    sections,
    isLoadingSections,
    isCreatePricing,
    storeList,
    isUploadingImage,
    stockUnits,
    isLoadingPricingTypes,
    isLoadingStockUnit,
    setIsCreatePricing,
    pricingTypes,
    removePricingForStoreProperty,
    addNewPricingForStoreProperty,
    compositeRootFormDetails,
    setCompositeRootFormDetails,
    booleanLabel,
    handleMultipleImageUpload,
    productImages,
    setProductImages,
    deleteImage,
    isUpdatingComposite,
    modalViews,
    resetModalViews,
    detectedPricing,
    selectedProducts,
  } = compositeProperties;

  const validation = Validation({
    rootFormDetails,
    storeProperties,
  });

  const {
    register,
    control,
    formState: { errors },
    handleSubmit,
    trigger,
    setValue,
  } = validation;

  const [showVatOverrideModal, setShowVatOverrideModal] = useState(false);
  const [getStorevatValues, setGetStorevatValues] = useState([]);
  const isInitialMount = useRef(true);
  const [StoreIdqueries, setStoreIdQueries] = useState();
  const params = useParams();
  const storeIdParam = useMemo(() => {
    if (Array.isArray(StoreIdqueries)) {
      const last = StoreIdqueries[StoreIdqueries.length - 1];
      return last != null ? Number(last) : undefined;
    }
    if (typeof StoreIdqueries === "string") {
      const stripped = StoreIdqueries.replace(/^\[|\]$/g, "");
      return stripped === "" ? undefined : Number(stripped);
    }
    if (StoreIdqueries && typeof StoreIdqueries === "object") {
      return (
        Number(StoreIdqueries.value ?? StoreIdqueries.id ?? NaN) || undefined
      );
    }
    return StoreIdqueries != null ? Number(StoreIdqueries) : undefined;
  }, [StoreIdqueries]);

  const products_details = (selectedProducts || [])
    .map((p) => ({
      product_id: p?.product_id ?? p?.itemId ?? null,
      quantity: Number(p?.stock_quantity ?? 1),
    }))
    .filter((p) => p.product_id != null);

  const variants_details = (selectedProducts || [])
    .map((v) => ({
      variant_id: v?.variant_id ?? null,
      quantity: Number(v?.quantity ?? 1),
    }))
    .filter((v) => v.variant_id != null);

  const { data: latestBatchByProductForStore, refetch: refetchLatestBatch } =
    useGetOrderBatchesCostPrice({
      data: {
        products_details,
        variants_details,
        store_id: storeIdParam ?? storeList?.[0]?.id,
      },
      isEnabled: storeIdParam !== undefined,
    });

  const applyCalculatedCosts = () => {
    const apiCost =
      latestBatchByProductForStore?.data?.cost_price ??
      latestBatchByProductForStore?.cost_price;

    if (apiCost == null || Number.isNaN(Number(apiCost))) return;

    const costValue = Number(apiCost).toFixed(2);

    const newStoreProperties = storeProperties.map((sp) => {
      if (
        Number(sp.store_id) === Number(storeIdParam) &&
        sp.auto_calculated !== false &&
        (!sp.cost_price || sp.auto_calculated === true)
      ) {
        return { ...sp, cost_price: costValue, auto_calculated: true };
      }
      return sp;
    });

    setStoreProperties(newStoreProperties);
    setIsStorePropertiesUpdated(true);
  };

  useEffect(() => {
    if (
      isInitialMount.current &&
      selectedProducts?.length > 0 &&
      storeProperties?.length > 0 &&
      latestBatchByProductForStore &&
      (latestBatchByProductForStore.data?.cost_price ??
        latestBatchByProductForStore.cost_price) != null
    ) {
      applyCalculatedCosts();
      isInitialMount.current = false;
    }
  }, [
    latestBatchByProductForStore,
    selectedProducts,
    storeProperties,
    storeIdParam,
  ]);

  useEffect(() => {
    window.setProductFormValue = setValue;
  }, []);

  useEffect(() => {
    if (isEditMode && rootFormDetails?.stores && storeList) {
      const storesWithVat = rootFormDetails.stores.map((selectedStore) => {
        const matchedStore = storeList.find(
          (store) => store.id === selectedStore.value,
        );
        return {
          ...selectedStore,
          vat: matchedStore?.vat,
        };
      });
      setGetStorevatValues(storesWithVat);
    }
  }, [isEditMode, rootFormDetails?.stores, storeList]);

  useEffect(() => {
    if (storeIdParam !== undefined) {
      refetchLatestBatch();
    }
  }, [storeIdParam, selectedProducts]);

  return (
    <>
      <motion.div
        initial={{ y: 100 }}
        animate={{ y: 0 }}
        className={`${styles.compositeDetailsForm} ${globalStyles.pageColumnGaps}`}
      >
        <div>
          <h4>Composite Details</h4>
          <p>
            Please provide the following information about your composite
            product
          </p>
        </div>

        <div className="d-flex" style={{ gap: "20px" }}>
          <secondaryComponents.WalletOption
            title="On Demand (Deduct on Sale)"
            subtitle="No production needed. Selling this item automatically deducts ingredients based on the recipe."
            onChange={(val) => {
              setCompositeRootFormDetails((prevState) => ({
                ...prevState,
                type: val,
              }));
            }}
            name="compositeType"
            checked={compositeRootFormDetails?.type === "ON_DEMAND"}
            value={"ON_DEMAND"}
            style={{ maxWidth: "none" }}
            isDisabled={isEditMode}
          />
          <secondaryComponents.WalletOption
            title="Produce in Advance (Stocked)"
            subtitle="Produce this item ahead of time and keep it in stock. Production deducts ingredients and adds the finished quantity you can track and sell."
            onChange={(val) => {
              setCompositeRootFormDetails((prevState) => ({
                ...prevState,
                type: val,
              }));
            }}
            name="compositeType"
            checked={compositeRootFormDetails?.type === "STOCKED"}
            value={"STOCKED"}
            style={{ maxWidth: "none" }}
            isDisabled={isEditMode}
          />
        </div>

        <form
          id="compositeForm"
          className={styles.formSection}
          onSubmit={handleSubmit(createOrUpdateProduct)}
        >
          <div className={sharedStyles.topFormSection}>
            <div className={sharedStyles.topFormSection__left}>
              {/* product name */}
              <primaryComponents.InputFieldV2
                name="productName"
                control={control}
                register={register}
                placeholder={"Enter product name"}
                classNames="white"
                label="Composite Product Name"
                value={rootFormDetails.name}
                isRequired
                errorMessage={errors?.productName}
                onKeyUp={() => errors?.productName && trigger("productName")}
                onChange={({ target: { value } }) => {
                  setRootFormDetails((prevState) => ({
                    ...prevState,
                    name: value,
                  }));
                }}
              />

              <CategoryGroup
                validation={validation}
                rootFormDetails={rootFormDetails}
                setRootFormDetails={setRootFormDetails}
              />

              <div className={sharedStyles.flexArrange}>
                <secondaryComponents.LoaderHelper
                  isLoading={isLoadingSections}
                  classNames={"mt-2"}
                >
                  {sections?.length > 0 ? (
                    <primaryComponents.SelectFieldV2
                      name="section"
                      control={control}
                      label="Select section"
                      value={
                        sections?.find(
                          (section) => section.id === rootFormDetails?.section,
                        )?.name || "Select..."
                      }
                      options={sections}
                      nameKey="name"
                      idKey="id"
                      classNames="w-100"
                      onChange={(value) => {
                        setRootFormDetails((prevState) => ({
                          ...prevState,
                          section: value,
                        }));
                      }}
                      isSearchable={true}
                    />
                  ) : null}
                </secondaryComponents.LoaderHelper>
              </div>

              <div className={sharedStyles.flexArrange}>
                {/* unit */}
                <div className="w-100">
                  <div className="d-flex justify-content-between align-items-center">
                    <label>
                      <span className="error--text">*</span>Product Unit
                    </label>
                    <span
                      className="purple--text small-text-size cursor-pointer"
                      onClick={() => resetModalViews("isCreateUnit")}
                    >
                      Create unit
                    </span>
                  </div>
                  <primaryComponents.SelectFieldV2
                    name="unit"
                    control={control}
                    value={rootFormDetails.unit?.label || "Select..."}
                    options={stockUnits}
                    nameKey="unit"
                    idKey="id"
                    onChange={(value, label) => {
                      setRootFormDetails((prevState) => ({
                        ...prevState,
                        unit: { value, label },
                      }));
                    }}
                    classNames="w-100"
                    isDisabled={isLoadingStockUnit}
                  />
                </div>

                {/* show on sell mode */}
                <primaryComponents.SelectFieldV2
                  classNames="w-100 mt-2"
                  name="showProductOnSellMode"
                  control={control}
                  label="Show this service in sell mode?"
                  isRequired={true}
                  value={
                    booleanLabel[rootFormDetails.show_product_in_sell_mode]
                  }
                  options={[
                    { label: "Yes", value: true },
                    { label: "No", value: false },
                  ]}
                  nameKey="label"
                  idKey="value"
                  onChange={(value) => {
                    setRootFormDetails((prevState) => ({
                      ...prevState,
                      show_product_in_sell_mode: value,
                    }));
                  }}
                  isSearchable={false}
                />
              </div>

              {/* store field  */}
              <primaryComponents.MultipleSelectFieldV2
                name="stores"
                control={control}
                options={storeList}
                isRequired={true}
                includeSelectAll={true}
                value={rootFormDetails?.stores}
                placeholder="Select store..."
                errorMessage={errors?.stores}
                Creatable={true}
                idKey="id"
                nameKey="name"
                label="Select Store To Make Product Available At"
                onChange={(values) => {
                  setStoreIdQueries(values?.map((v) => v?.value));
                  const storesWithVat = values.map((selectedStore) => {
                    const matchedStore = storeList.find(
                      (store) => store.id === selectedStore.value,
                    );
                    return {
                      ...selectedStore,
                      vat: matchedStore?.vat,
                    };
                  });
                  setGetStorevatValues && setGetStorevatValues(storesWithVat);
                  setRootFormDetails((prevState) => ({
                    ...prevState,
                    stores: values,
                  }));
                  setIsStorePropertiesUpdated(true);
                }}
              />

              <div className={sharedStyles.flexArrange}>
                {/* product sku */}
                <primaryComponents.InputFieldV2
                  name="productSku"
                  control={control}
                  label="Product SKU"
                  placeholder="Enter SKU"
                  classNames="white"
                  value={rootFormDetails.sku}
                  errorMessage={errors?.productSku}
                  onKeyUp={() => errors?.productSku && trigger("productSku")}
                  onChange={({ target: { value } }) => {
                    setRootFormDetails((prevState) => ({
                      ...prevState,
                      sku: value,
                    }));
                  }}
                />
                {/* bar code */}
                {!rootFormDetails?.has_variant ? (
                  <primaryComponents.InputFieldV2
                    inputIcon={Icon.barCodeIcon}
                    name="barcode"
                    control={control}
                    label="Scan Barcode"
                    placeholder="Scan barcode"
                    classNames="white"
                    value={rootFormDetails.barcode}
                    errorMessage={errors?.barcode}
                    onKeyUp={(e) => {
                      if (e.key === "Enter") e.preventDefault();
                      errors?.barcode && trigger("barcode");
                    }}
                    onChange={(e) => {
                      setRootFormDetails((prevState) => ({
                        ...prevState,
                        barcode: e?.target?.value,
                      }));
                    }}
                  />
                ) : null}
              </div>

              <Vatform
                validation={validation}
                rootFormDetails={rootFormDetails}
                setRootFormDetails={setRootFormDetails}
              />
            </div>
            {/* profile section */}
            <ProductImageUploader
              images={productImages}
              onImagesChange={setProductImages}
              onUpload={handleMultipleImageUpload}
              onDeleteSaved={deleteImage}
              isUploading={isUploadingImage}
              itemId={String(params?.id)}
              itemType="composite_product"
              onReorder={reorderImages}
            />
          </div>

          <h5 className="mt-5">Composite Product Pricing(s)</h5>

          <div className={sharedStyles.bottomFormSection}>
            {/*  product without variants */}
            <>
              {storeProperties?.length > 0 ? (
                <>
                  {storeProperties?.map((item, storeIndex) => (
                    <div
                      className={sharedStyles.priceCover}
                      key={item.store_id}
                    >
                      <div>
                        <h6>Update {item?.store_name || ""} pricing</h6>
                        <div className={`${sharedStyles.priceSection}`}>
                          {/* cost price */}
                          {(!isEditMode ||
                            compositeRootFormDetails?.type === "ON_DEMAND" ||
                            (isEditMode &&
                              item?.returned_from_backend &&
                              compositeRootFormDetails?.type ===
                                "ON_DEMAND")) && (
                            <div
                              style={{
                                position: "relative",
                                marginRight: "10px",
                              }}
                            >
                              {(!isEditMode ||
                                compositeRootFormDetails?.type ===
                                  "ON_DEMAND") && (
                                <primaryComponents.InputFieldV2
                                  name={`storeProperties.${storeIndex}.cost_price`}
                                  control={control}
                                  type="number"
                                  step={0.01}
                                  label={`Cost Price (${
                                    storeList.find(
                                      (store) => store?.id === item?.store_id,
                                    )?.currency_symbol
                                  })`}
                                  placeholder="0"
                                  classNames="white"
                                  isRequired
                                  isDisabled={
                                    (storeIndex > 0 &&
                                      rootFormDetails?.use_same_pricing) ||
                                    (isEditMode && item?.returned_from_backend)
                                  }
                                  value={storeProperties[storeIndex].cost_price}
                                  errorMessage={
                                    errors?.storeProperties?.[storeIndex]
                                      ?.cost_price
                                  }
                                  onKeyUp={() =>
                                    errors?.storeProperties?.[storeIndex]
                                      ?.cost_price &&
                                    trigger(
                                      `storeProperties.${storeIndex}.cost_price`,
                                    )
                                  }
                                  onChange={({ target: { value } }) => {
                                    const newStoreProperties = [
                                      ...storeProperties,
                                    ];
                                    newStoreProperties[storeIndex].cost_price =
                                      value;

                                    if (
                                      value !==
                                      latestBatchByProductForStore?.data
                                        ?.cost_price
                                    ) {
                                      newStoreProperties[
                                        storeIndex
                                      ].auto_calculated = false;
                                    }

                                    setStoreProperties(newStoreProperties);
                                    setIsStorePropertiesUpdated(true);
                                  }}
                                  floatError={false}
                                />
                              )}
                            </div>
                          )}
                          {isEditMode &&
                            item?.returned_from_backend &&
                            compositeRootFormDetails?.type === "STOCKED" && (
                              <div
                                className="mt-3"
                                style={{
                                  border: "2px solid #eee",
                                  borderRadius: "6px",
                                  padding: "7px",
                                  minWidth: "180px",
                                }}
                              >
                                <p className="small-text-size mb-2">
                                  Verify <strong>CP</strong> of this item on:
                                </p>
                                <span
                                  className="purple--text cursor-pointer small-text-size d-flex align-items-center"
                                  onClick={() =>
                                    navigate(
                                      pathConstants.INVENTORY_PRODUCTS_BATCH +
                                        `?storeId=${item?.store_id}&query=${rootFormDetails?.name}`,
                                    )
                                  }
                                >
                                  <span className="mr-2">
                                    Product batch page{" "}
                                  </span>{" "}
                                  <ArrowCircle stroke="#000" />
                                </span>
                              </div>
                            )}

                          {compositeRootFormDetails?.type === "STOCKED" ? (
                            <>
                              {!isEditMode ? (
                                // stock quantity
                                <primaryComponents.InputFieldV2
                                  name={`storeProperties.${storeIndex}.stock_quantity`}
                                  control={control}
                                  type="number"
                                  step={0.01}
                                  min={0.01}
                                  label="Initial Stock"
                                  placeholder="0"
                                  classNames="white"
                                  value={
                                    storeProperties[storeIndex]
                                      ?.stock_quantity || ""
                                  }
                                  errorMessage={
                                    errors?.storeProperties?.[storeIndex]
                                      ?.stock_quantity
                                  }
                                  onKeyUp={() =>
                                    errors?.storeProperties?.[storeIndex]
                                      ?.stock_quantity &&
                                    trigger(
                                      `storeProperties.${storeIndex}.stock_quantity`,
                                    )
                                  }
                                  onChange={({ target: { value } }) => {
                                    const newStoreProperties = [
                                      ...storeProperties,
                                    ];
                                    newStoreProperties[
                                      storeIndex
                                    ].stock_quantity =
                                      value === "" ? undefined : value;
                                    setStoreProperties(newStoreProperties);
                                    setIsStorePropertiesUpdated(true);
                                  }}
                                />
                              ) : null}

                              {/* minimum stock quantity */}
                              <primaryComponents.InputFieldV2
                                name={`storeProperties.${storeIndex}.minimum_stock_quantity`}
                                control={control}
                                type="number"
                                step={0.01}
                                min={0}
                                label="Stock Level Alert"
                                placeholder="0"
                                classNames="white"
                                value={
                                  storeProperties[storeIndex]
                                    ?.minimum_stock_quantity || ""
                                }
                                errorMessage={
                                  errors?.storeProperties?.[storeIndex]
                                    ?.minimum_stock_quantity
                                }
                                onKeyUp={() =>
                                  errors?.storeProperties?.[storeIndex]
                                    ?.minimum_stock_quantity &&
                                  trigger(
                                    `storeProperties.${storeIndex}.minimum_stock_quantity`,
                                  )
                                }
                                onChange={({ target: { value } }) => {
                                  const newStoreProperties = [
                                    ...storeProperties,
                                  ];
                                  newStoreProperties[
                                    storeIndex
                                  ].minimum_stock_quantity =
                                    value === "" ? undefined : value;
                                  setStoreProperties(newStoreProperties);
                                  setIsStorePropertiesUpdated(true);
                                }}
                              />

                              {/* Date input*/}
                              {storeProperties[storeIndex]?.stock_quantity >
                                0 || isEditMode ? (
                                <primaryComponents.DateInputV2
                                  name={`storeProperties.${storeIndex}.expiry_date`}
                                  control={control}
                                  label="Expiry Date"
                                  placeholder="Expiry date"
                                  classNames="white"
                                  value={
                                    storeProperties?.[storeIndex]
                                      ?.expiry_date || ""
                                  }
                                  errorMessage={
                                    errors?.storeProperties?.[storeIndex]
                                      ?.expiry_date
                                  }
                                  onKeyUp={() =>
                                    errors?.storeProperties?.[storeIndex]
                                      ?.expiry_date &&
                                    trigger(
                                      `storeProperties.${storeIndex}.expiry_date`,
                                    )
                                  }
                                  onChange={({ target: { value } }) => {
                                    const newStoreProperties = [
                                      ...storeProperties,
                                    ];
                                    newStoreProperties[storeIndex].expiry_date =
                                      value === "" ? undefined : value;
                                    setStoreProperties(newStoreProperties);
                                    setIsStorePropertiesUpdated(true);
                                  }}
                                />
                              ) : null}
                            </>
                          ) : null}

                          {item?.pricings?.map(
                            (pricingProperty, pricingIndex) => {
                              const pricingDetailsProperty = pricingTypes?.find(
                                (pricingItem) =>
                                  pricingItem?.id ===
                                  pricingProperty?.pricing_type_id,
                              );

                              const storeCurrency = storeList.find(
                                (store) => store?.id === item?.store_id,
                              )?.currency_symbol;

                              const realSellingPrice =
                                rootFormDetails?.include_vat_in_price &&
                                rootFormDetails?.vat_enabled
                                  ? Number(pricingProperty?.price) /
                                    (1 + Number(rootFormDetails?.vat) / 100)
                                  : Number(pricingProperty?.price);

                              const discountAmount =
                                (Number(pricingProperty?.discount_rate) *
                                  realSellingPrice) /
                                100;

                              const discountedAmount =
                                realSellingPrice - discountAmount;

                              const vatAmount = rootFormDetails?.vat_enabled
                                ? (Number(discountedAmount) *
                                    Number(rootFormDetails?.vat)) /
                                    100 || 0
                                : 0;

                              return (
                                <>
                                  <div
                                    className={`${sharedStyles.priceSection} ${globalStyles.borderTopPurple} ${globalStyles.borderTop} mt-0 pt-3`}
                                    style={{ minWidth: "100%" }}
                                    key={pricingProperty?.uuid}
                                  >
                                    {/* pricing type */}
                                    <secondaryComponents.LoaderHelper
                                      isLoading={isLoadingPricingTypes}
                                      classNames={"mt-2"}
                                    >
                                      <div style={{ maxWidth: "180px" }}>
                                        <label htmlFor="">
                                          <span className="text-danger">*</span>

                                          <span>
                                            Price Type
                                            {item?.pricings?.length ===
                                            pricingIndex + 1 ? (
                                              <>
                                                {" ( "}
                                                <span
                                                  className="purple--text"
                                                  style={{
                                                    textDecoration: "underline",
                                                    cursor: "pointer",
                                                  }}
                                                  onClick={() =>
                                                    setIsCreatePricing(true)
                                                  }
                                                >
                                                  Create new
                                                </span>
                                                {" )"}
                                              </>
                                            ) : null}
                                          </span>
                                        </label>
                                        <primaryComponents.SelectFieldV2
                                          name={`storeProperties.${storeIndex}.pricings.${pricingIndex}.pricing_type_id`}
                                          control={control}
                                          giveControl={false}
                                          value={{
                                            label:
                                              pricingDetailsProperty?.label,
                                            value: pricingDetailsProperty?.id,
                                          }}
                                          options={pricingTypes}
                                          nameKey="label"
                                          idKey="id"
                                          onChange={(value) => {
                                            const newStoreProperties = [
                                              ...storeProperties,
                                            ];
                                            newStoreProperties[
                                              storeIndex
                                            ].pricings[
                                              pricingIndex
                                            ].pricing_type_id = value;
                                            setStoreProperties(
                                              newStoreProperties,
                                            );
                                            setIsStorePropertiesUpdated(true);
                                          }}
                                          errorMessage={
                                            errors?.storeProperties?.[
                                              storeIndex
                                            ]?.pricings?.[pricingIndex]
                                              ?.pricing_type_id
                                          }
                                          isDisabled={
                                            (storeIndex > 0 &&
                                              rootFormDetails?.use_same_pricing) ||
                                            pricingIndex === 0
                                          }
                                          isSearchable={false}
                                        />
                                      </div>
                                    </secondaryComponents.LoaderHelper>

                                    {/* selling price */}
                                    <div style={{ position: "relative" }}>
                                      <primaryComponents.InputFieldV2
                                        name={`storeProperties.${storeIndex}.pricings.${pricingIndex}.price`}
                                        control={control}
                                        type="number"
                                        step={0.01}
                                        label={`Selling Amount (${
                                          storeCurrency
                                        })`}
                                        placeholder="0"
                                        classNames="white"
                                        isRequired
                                        isDisabled={
                                          storeIndex > 0 &&
                                          rootFormDetails?.use_same_pricing
                                        }
                                        value={pricingProperty?.price}
                                        errorMessage={
                                          errors?.storeProperties?.[storeIndex]
                                            ?.pricings?.[pricingIndex]?.price
                                        }
                                        onKeyUp={() =>
                                          errors?.storeProperties?.[storeIndex]
                                            ?.pricings?.[pricingIndex]?.price &&
                                          trigger(
                                            `storeProperties.${storeIndex}.pricings.${pricingIndex}.price`,
                                          )
                                        }
                                        onChange={({ target: { value } }) => {
                                          const newStoreProperties = [
                                            ...storeProperties,
                                          ];
                                          newStoreProperties[
                                            storeIndex
                                          ].pricings[pricingIndex].price =
                                            value;
                                          setStoreProperties(
                                            newStoreProperties,
                                          );
                                          setIsStorePropertiesUpdated(true);
                                        }}
                                      />
                                    </div>

                                    {/* discount */}
                                    <primaryComponents.PercentField
                                      label="Discount"
                                      name={`storeProperties.${storeIndex}.pricings.${pricingIndex}.discount_rate`}
                                      control={control}
                                      value={pricingProperty?.discount_rate}
                                      isDisabled={
                                        storeIndex > 0 &&
                                        rootFormDetails?.use_same_pricing
                                      }
                                      onChange={({ percentValue }) => {
                                        const newStoreProperties = [
                                          ...storeProperties,
                                        ];
                                        newStoreProperties[storeIndex].pricings[
                                          pricingIndex
                                        ].discount_rate = percentValue;
                                        setStoreProperties(newStoreProperties);
                                        setIsStorePropertiesUpdated(true);
                                      }}
                                      dividedValue={
                                        pricingProperty?.price === ""
                                          ? 0
                                          : realSellingPrice
                                      }
                                    />

                                    {pricingIndex > 0 ? (
                                      <div
                                        className="d-flex align-items-center my-auto"
                                        style={{
                                          minWidth: "fit-content",
                                          maxWidth: "fit-content",
                                        }}
                                      >
                                        <button
                                          type="button"
                                          className="btn btn--outline-red"
                                          style={{
                                            height: "30px",
                                            width: "28px",
                                            padding: "0",
                                            borderRadius: "3px",
                                          }}
                                          onClick={() =>
                                            removePricingForStoreProperty(
                                              storeIndex,
                                              pricingIndex,
                                            )
                                          }
                                        >
                                          <img src={Icon.deleteIcon} alt="" />
                                        </button>
                                      </div>
                                    ) : null}
                                  </div>

                                  <div
                                    className="d-flex flex-wrap pb-2"
                                    style={{
                                      columnGap: "50px",
                                      rowGap: "15px",
                                      minWidth: "auto",
                                      maxWidth: "none",
                                    }}
                                  >
                                    {rootFormDetails?.vat_enabled && (
                                      <>
                                        <div>
                                          <h6>VAT Amount</h6>
                                          <p>
                                            {storeCurrency}
                                            {vatAmount.toFixed(2)}
                                          </p>
                                        </div>

                                        <div>
                                          <h6>Selling Price (VAT Exclusive)</h6>
                                          <p>
                                            {storeCurrency}
                                            {discountedAmount.toFixed(2)}
                                          </p>
                                        </div>

                                        <div>
                                          <h6>Selling Price (VAT Inclusive)</h6>
                                          <p>
                                            {storeCurrency}
                                            {(
                                              discountedAmount + vatAmount
                                            ).toFixed(2)}
                                          </p>
                                        </div>
                                      </>
                                    )}

                                    {!isEditMode ||
                                    compositeRootFormDetails?.type ===
                                      "ON_DEMAND" ? (
                                      <div>
                                        <h6>Profit</h6>
                                        <p>
                                          {storeCurrency}
                                          {(
                                            discountedAmount - item?.cost_price
                                          ).toFixed(2)}
                                        </p>
                                      </div>
                                    ) : null}
                                  </div>
                                </>
                              );
                            },
                          )}
                        </div>

                        {/* use across */}
                        <div className={sharedStyles.useAcrossSection}>
                          <a
                            className="purple--text"
                            style={{ cursor: "pointer" }}
                            onClick={() =>
                              addNewPricingForStoreProperty(storeIndex)
                            }
                          >
                            + Add another price option
                          </a>
                          {storeIndex === 0 ? (
                            <label
                              className="d-flex align-items-center"
                              style={{ cursor: "pointer" }}
                              htmlFor="useSamePricing"
                            >
                              <primaryComponents.Checkbox
                                name="useSamePricing"
                                id="useSamePricing"
                                isChecked={rootFormDetails?.use_same_pricing}
                                onChange={(value) => {
                                  setRootFormDetails((prevState) => ({
                                    ...prevState,
                                    use_same_pricing: value,
                                  }));
                                  if (value) {
                                    if (!rootFormDetails.has_variant)
                                      setIsStorePropertiesUpdated(true);
                                  }
                                }}
                              />
                              <span className="ml-2 mt-1">
                                Use this pricing properties across other stores
                              </span>
                            </label>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              ) : (
                <h5 className="text-center my-4">
                  Please select stores to proceed with pricing
                </h5>
              )}
            </>
          </div>
        </form>

        <div className={styles.buttonSection}>
          <primaryComponents.Button
            classNames={"btn btn--outline"}
            style={{ width: "200px" }}
            onClick={() => setCurrentStep(0)}
          >
            Back
          </primaryComponents.Button>
          <primaryComponents.Button
            classNames={"btn btn--primary"}
            form="compositeForm"
            type="submit"
            isDisabled={isSubmiting || isUpdatingComposite}
            isLoading={isSubmiting || isUpdatingComposite}
            loadingText={isEditMode ? "Updating..." : "Creating..."}
          >
            {isEditMode
              ? "Update Composite Product"
              : "Create Composite Product"}
          </primaryComponents.Button>
        </div>
      </motion.div>

      {/* create pricing */}
      <secondaryComponents.Modal
        isActive={isCreatePricing}
        closeModal={() => setIsCreatePricing(false)}
        width={400}
      >
        <secondaryComponents.CreatePricingType
          closeWidget={() => setIsCreatePricing(false)}
        />
      </secondaryComponents.Modal>

      {/* unit form */}
      <secondaryComponents.Modal
        isActive={modalViews?.isCreateUnit}
        closeModal={() => resetModalViews("isCreateUnit", false)}
        width={400}
      >
        <secondaryComponents.ProductUnitForm
          closeWidget={() => resetModalViews("isCreateUnit", false)}
        />
      </secondaryComponents.Modal>

      <secondaryComponents.Modal
        isActive={modalViews?.isWarning}
        closeModal={() => resetModalViews("isWarning", false)}
        width={500}
      >
        <WarningDialog
          closeModal={() => resetModalViews("isWarning", false)}
          title={"Pricing Warning"}
          handler={createOrUpdateProduct}
        >
          <PricingWarning detectedPricing={detectedPricing} />
        </WarningDialog>
      </secondaryComponents.Modal>

      {/* VAT override modal */}
      <secondaryComponents.Modal
        isActive={showVatOverrideModal}
        closeModal={() => {
          setRootFormDetails((prev) => ({ ...prev, vat_enabled: false }));
          setShowVatOverrideModal(false);
        }}
        width={500}
      >
        <WarningDialog
          closeModal={() => {
            setShowVatOverrideModal(false);
            setRootFormDetails((prev) => ({ ...prev, vat_enabled: false }));
          }}
          title={"Store VAT Exists"}
          handler={() => {
            setRootFormDetails((prev) => ({ ...prev, vat_enabled: true }));
            setShowVatOverrideModal(false);
          }}
          continueText="Continue"
          cancelText="Cancel"
        >
          <div style={{ padding: "16px", fontSize: "16px" }}>
            A store VAT already exists for this product under{" "}
            <b>
              {(getStorevatValues || [])
                .filter((s) => s.vat && Number(s.vat) > 0)
                .map((s) => s.label || s.name)
                .join(", ")}
            </b>
            .<br />
            <br />
            Would you like to proceed with creating a product VAT as well?
            <br />
            <span style={{ color: "#a00", fontWeight: 500 }}>
              Please note that once a product VAT is set, it will be used at
              checkout in SellMode instead of the store-level VAT.
            </span>
          </div>
        </WarningDialog>
      </secondaryComponents.Modal>
    </>
  );
};
export default CompositeDetailsForm;
